import { AxiosError } from 'axios'
import { apiErrorMessage } from '@/apiErrorMessage'
import { Ref } from 'vue'

export function isAxiosError (error: any): error is AxiosError {
  return error !== undefined &&
    error !== null &&
    error.isAxiosError !== undefined
}

/**
 * エラー発生時アクション
 */
interface ErrorAction {
  (message: string, error: unknown): void;
}

interface Handler {
  handle (): void;
}

/**
 * たらい回し管理データ
 */
class ErrorHandleChainContext {
  constructor (error: unknown) {
    this.error = error
    this.handledCount = 0
  }

  readonly error: unknown

  // ハンドル回数。0だった場合はハンドルできていないので、後に例外を投げる。
  handledCount: number
}

/**
 * エラーハンドラの基本機能を実装
 */
class DefaultErrorHandler implements Handler {
  constructor (context: ErrorHandleChainContext, defaultMessage: string, action: ErrorAction) {
    this.chainContext = context
    this.defaultMessage = defaultMessage
    this.action = action
  }

  readonly chainContext: ErrorHandleChainContext
  readonly action: ErrorAction
  readonly defaultMessage: string

  invokeAction () {
    this.action(this.getMessage(), this.chainContext.error)
    ++this.chainContext.handledCount
  }

  handle () {
    this.invokeAction()
  }

  getMessage (): string {
    return this.defaultMessage
  }
}

/**
 * ネットワークエラーのとき、ハンドルを実行します。
 */
class NetworkErrorHandler extends DefaultErrorHandler {
  handle () {
    if (!isAxiosError(this.chainContext.error)) {
      return
    }
    const code = this.chainContext.error.code
    if (code === undefined || code === null) {
      return
    }
    if (code === AxiosError.ERR_NETWORK) {
      this.invokeAction()
    }
  }
}

type HasMessageType = {
  message: string;
}

export function hasMessage (target: any): target is HasMessageType {
  return target !== undefined &&
    target !== null &&
    typeof target.message === 'string'
}

/**
 * 特定のHTTPステータスのエラーのとき、ハンドルを実行します。
 */
class HttpStatusCodeErrorHandler extends DefaultErrorHandler {
  constructor (chainContext: ErrorHandleChainContext, defaultMessage: string, action: ErrorAction, statusCode: number) {
    super(chainContext, defaultMessage, action)
    this.expectedStatusCode = statusCode
  }

  readonly expectedStatusCode: number

  handle () {
    if (!isAxiosError(this.chainContext.error)) {
      return
    }
    const response = this.chainContext.error.response
    if (response === undefined || response === null) {
      return
    }
    if (response.status === this.expectedStatusCode) {
      this.invokeAction()
    }
  }

  override getMessage (): string {
    const e = this.chainContext.error
    if (!isAxiosError(e) ||
      e.response === undefined ||
      e.response.data === undefined) {
      return super.getMessage()
    }
    if (hasMessage(e.response.data)) {
      return e.response.data.message
    }
    return super.getMessage()
  }
}

/**
 * HTTPリクエストに失敗したときのエラーハンドラを構築します
 */
class RequestErrorHandlerBuilder implements Handler {
  constructor (error: unknown) {
    this.chainContext = new ErrorHandleChainContext(error)
    this.handlers = new Map<string, Handler>()
  }

  private readonly chainContext: ErrorHandleChainContext
  private handlers: Map<string, Handler>
  private defaultHandler: DefaultErrorHandler | null = null

  /**
   * ネットワークエラーに対応するハンドラを登録する
   * @param action
   */
  networkError (action: ErrorAction): RequestErrorHandlerBuilder {
    const handler = new NetworkErrorHandler(
      this.chainContext,
      apiErrorMessage.networkError,
      action
    )
    this.handlers.set(NetworkErrorHandler.name, handler)
    return this
  }

  /**
   * 401 Unauthorizedエラーに対応するハンドラを登録する
   * @param action
   */
  unauthorized (action: ErrorAction): RequestErrorHandlerBuilder {
    const handler = new HttpStatusCodeErrorHandler(
      this.chainContext,
      apiErrorMessage.sessionTimeout,
      action,
      401
    )
    const key = `${HttpStatusCodeErrorHandler.name}_401`
    this.handlers.set(key, handler)
    return this
  }

  /**
   * 400 Bad Requestエラーに対応するハンドラを登録する
   * @param action
   */
  badRequest (action: ErrorAction): RequestErrorHandlerBuilder {
    const handler = new HttpStatusCodeErrorHandler(
      this.chainContext,
      apiErrorMessage.badRequest,
      action,
      400
    )
    const key = `${HttpStatusCodeErrorHandler.name}_400`
    this.handlers.set(key, handler)
    return this
  }

  /**
   * 500 Internal Server Errorエラーに対応するハンドラを登録する
   * @param action
   */
  internalServerError (action: ErrorAction): RequestErrorHandlerBuilder {
    const handler = new HttpStatusCodeErrorHandler(
      this.chainContext,
      apiErrorMessage.internalServerError,
      action,
      500
    )
    const key = `${HttpStatusCodeErrorHandler.name}_500`
    this.handlers.set(key, handler)
    return this
  }

  /**
   * 想定外のエラーに対応するハンドラを登録する
   * @param action
   */
  unexpectedError (action: ErrorAction): RequestErrorHandlerBuilder {
    this.defaultHandler = new DefaultErrorHandler(
      this.chainContext,
      apiErrorMessage.unexpectedError,
      action
    )
    return this
  }

  /**
   * ハンドリングします。
   */
  handle (): void {
    this.handlers.forEach(h => h.handle())
    if (this.chainContext.handledCount !== 0) {
      return
    }
    if (this.defaultHandler) {
      this.defaultHandler.handle()
    } else {
      throw new Error('RequestErrorHandlerBuilder has unhandled error:\n' + this.chainContext.error)
    }
  }
}

/**
 * HTTPリクエストに失敗したときのエラーハンドラ機能を提供します。
 */
export class RequestErrorHandler {
  /**
   * ビルダーを生成します。
   * @param error
   */
  static builder (error: unknown): RequestErrorHandlerBuilder {
    return new RequestErrorHandlerBuilder(error)
  }
}

export interface RequestErrorMessageState {
  errorMessage: string | null;
}

function isRequestErrorMessageState (target: any): target is RequestErrorMessageState {
  return target !== undefined &&
    target !== null &&
    typeof target.errorMessage === 'string'
}

function setMessage (message: string, state: RequestErrorMessageState | Ref<string | null>) {
  if (isRequestErrorMessageState(state)) {
    state.errorMessage = message
  } else {
    state.value = message
  }
}

/**
 * デフォルトのエラーハンドラビルダーを生成します。
 *
 * ほとんどの画面で、catch句内で同じコードを書く可能性があるので、デフォルトの処理を用意した。
 *
 * @param state
 * @param error
 */
export function buildDefaultRequestErrorHandler (state: RequestErrorMessageState | Ref<string | null>, error: unknown): RequestErrorHandlerBuilder {
  return RequestErrorHandler.builder(error)
    .networkError(message => {
      setMessage(message, state)
    })
    .unauthorized(message => {
      setMessage(message, state)
    })
    .badRequest(message => {
      setMessage(message, state)
    })
    .internalServerError(message => {
      setMessage(message, state)
    })
    .unexpectedError((message, error) => {
      setMessage(message, state)
      console.error(error)
    })
}
